import React, { createContext, useState, useContext, useCallback } from 'react';

/**
 * Context for centralized error and loading state management
 */
export const ErrorContext = createContext();

/**
 * Provider for error and loading states across the Group Intake process
 */
export const ErrorProvider = ({ children }) => {
  // Centralized error state
  const [errors, setErrors] = useState({});
  
  // Validation state for form sections
  const [validationState, setValidationState] = useState({
    group: false,
    facilities: {},
    listings: {}
  });
  
  // Loading states for different operations
  const [loadingStates, setLoadingStates] = useState({
    group: false,
    facilities: false,
    listings: false,
    submission: false,
    validation: false
  });
  
  // Last save time for debouncing
  const [lastSaveTime, setLastSaveTime] = useState(null);
  
  // Autosave status message
  const [autosaveStatus, setAutosaveStatus] = useState({ message: '', type: '' });
  
  // Debounce timer references
  const [groupSaveTimers, setGroupSaveTimers] = useState({});
  const [facilitySaveTimers, setFacilitySaveTimers] = useState({});
  const [listingSaveTimers, setListingSaveTimers] = useState({});
  
  // Add an error
  const addError = useCallback((key, message) => {
    setErrors(prev => ({ ...prev, [key]: message }));
  }, []);
  
  // Clear specific error
  const clearError = useCallback((key) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });
  }, []);
  
  // Clear all errors
  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);
  
  // Set loading state for a specific operation
  const setLoading = useCallback((operation, isLoading) => {
    setLoadingStates(prev => ({ ...prev, [operation]: isLoading }));
  }, []);
  
  // Update validation state
  const updateValidation = useCallback((section, status, id = null) => {
    setValidationState(prev => {
      if (section === 'group') {
        return { ...prev, group: status };
      } else if (section === 'facility' && id !== null) {
        return { 
          ...prev, 
          facilities: { ...prev.facilities, [id]: status } 
        };
      } else if (section === 'listing' && id !== null) {
        return { 
          ...prev, 
          listings: { ...prev.listings, [id]: status } 
        };
      }
      return prev;
    });
  }, []);
  
  // Start autosave timer with debounce
  const startAutosaveTimer = useCallback((section, id, field, value, callback) => {
    const DEBOUNCE_DELAY = 2000; // 2 seconds
    
    // Clear existing timer for this field if any
    let timers;
    if (section === 'group') {
      timers = groupSaveTimers;
      if (timers[field]) {
        clearTimeout(timers[field]);
      }
    } else if (section === 'facility') {
      timers = facilitySaveTimers;
      const timerKey = `${id}_${field}`;
      if (timers[timerKey]) {
        clearTimeout(timers[timerKey]);
      }
    } else if (section === 'listing') {
      timers = listingSaveTimers;
      const timerKey = `${id}_${field}`;
      if (timers[timerKey]) {
        clearTimeout(timers[timerKey]);
      }
    }
    
    // Set new timer
    const now = Date.now();
    const timeSinceLastSave = now - (lastSaveTime || 0);
    
    if (timeSinceLastSave < DEBOUNCE_DELAY) {
      const remainingDelay = DEBOUNCE_DELAY - timeSinceLastSave;
      
      // Create a new timer
      const timerId = setTimeout(() => {
        setAutosaveStatus({ message: 'Saving changes...', type: 'info' });
        
        // Execute the save callback
        try {
          callback(field, value);
          setLastSaveTime(Date.now());
          setAutosaveStatus({ message: 'Changes saved', type: 'success' });
          
          // Clear status after a delay
          setTimeout(() => {
            setAutosaveStatus({ message: '', type: '' });
          }, 3000);
        } catch (error) {
          console.error('Error in autosave:', error);
          setAutosaveStatus({ message: 'Failed to save changes', type: 'error' });
        }
      }, remainingDelay);
      
      // Store the timer
      if (section === 'group') {
        setGroupSaveTimers(prev => ({ ...prev, [field]: timerId }));
      } else if (section === 'facility') {
        const timerKey = `${id}_${field}`;
        setFacilitySaveTimers(prev => ({ ...prev, [timerKey]: timerId }));
      } else if (section === 'listing') {
        const timerKey = `${id}_${field}`;
        setListingSaveTimers(prev => ({ ...prev, [timerKey]: timerId }));
      }
    } else {
      // No need to debounce, save immediately
      setAutosaveStatus({ message: 'Saving changes...', type: 'info' });
      
      try {
        callback(field, value);
        setLastSaveTime(Date.now());
        setAutosaveStatus({ message: 'Changes saved', type: 'success' });
        
        // Clear status after a delay
        setTimeout(() => {
          setAutosaveStatus({ message: '', type: '' });
        }, 3000);
      } catch (error) {
        console.error('Error in immediate save:', error);
        setAutosaveStatus({ message: 'Failed to save changes', type: 'error' });
      }
    }
  }, [groupSaveTimers, facilitySaveTimers, listingSaveTimers, lastSaveTime]);
  
  // Get overall loading state
  const isLoading = Object.values(loadingStates).some(state => state === true);
  
  // Context value
  const contextValue = {
    errors,
    setErrors,
    addError,
    clearError,
    clearAllErrors,
    validationState,
    updateValidation,
    loadingStates,
    setLoading,
    isLoading,
    autosaveStatus,
    startAutosaveTimer
  };
  
  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

export const useError = () => useContext(ErrorContext);
